import { useCallback, useMemo, useState } from 'react';
import { getPathSteps, getVisibleSteps } from './stepRouting';
import type { AnchorCategory, PublishDestination, PublishDevice, StepId } from './types';

export interface PublishFlowState {
  devices: PublishDevice[];
  currentStepIndex: number;
}

export interface UsePublishFlowArgs {
  anchorCategory: AnchorCategory | undefined;
  initialDestination?: PublishDestination;
  // Step to land on when the flow starts or resets. Entry points that already
  // made the destination choice (like "Update submission") skip past it.
  initialStep?: StepId;
}

export interface UsePublishFlowResult {
  destination: PublishDestination;
  selectTemplateDestination: () => void;
  devices: PublishDevice[];
  toggleDevice: (d: PublishDevice) => void;
  pathSteps: readonly StepId[];
  visibleSteps: StepId[];
  currentStepIndex: number;
  currentStep: StepId;
  goNext: () => void;
  goBack: () => void;
  goToStep: (id: StepId) => void;
  canGoBack: boolean;
  canGoNext: boolean;
  isLastStep: boolean;
  reset: () => void;
}

const DEFAULT_DEVICES: PublishDevice[] = ['app'];

interface DestinationState {
  destination: PublishDestination;
  devices: PublishDevice[];
  // What the audience group held before Template took over, so toggling back
  // restores the creator's selection instead of the default.
  audienceDevices: PublishDevice[];
}

const INITIAL_DESTINATION: DestinationState = {
  destination: 'audience',
  devices: DEFAULT_DEVICES,
  audienceDevices: DEFAULT_DEVICES,
};

const startingState = (initialDestination: PublishDestination): DestinationState =>
  initialDestination === 'template'
    ? { destination: 'template', devices: [], audienceDevices: DEFAULT_DEVICES }
    : INITIAL_DESTINATION;

export function usePublishFlow({
  anchorCategory,
  initialDestination = 'audience',
  initialStep,
}: UsePublishFlowArgs): UsePublishFlowResult {
  const startingStepIndex = useCallback(
    (start: DestinationState): number => {
      if (!initialStep) return 0;
      const steps = getVisibleSteps({
        devices: start.devices,
        anchorCategory,
        destination: start.destination,
      });
      return Math.max(0, steps.indexOf(initialStep));
    },
    [initialStep, anchorCategory],
  );

  const [{ destination, devices }, setTarget] = useState<DestinationState>(() =>
    startingState(initialDestination),
  );
  const [currentStepIndex, setCurrentStepIndex] = useState(() =>
    startingStepIndex(startingState(initialDestination)),
  );

  const pathSteps = useMemo(() => getPathSteps(destination), [destination]);
  const visibleSteps = useMemo(
    () => getVisibleSteps({ devices, anchorCategory, destination }),
    [devices, anchorCategory, destination],
  );

  const currentStep = visibleSteps[Math.min(currentStepIndex, visibleSteps.length - 1)];
  const isLastStep = currentStepIndex >= visibleSteps.length - 1;
  const canGoBack = currentStepIndex > 0;
  const onChoosePlatform = currentStep === 'choose-platform';
  const hasDestination = destination === 'template' || devices.length > 0;
  const canGoNext = !isLastStep && (onChoosePlatform ? hasDestination : true);

  // The two destination groups are mutually exclusive: picking a platform tile
  // drops out of the template path, and picking the template tile clears the
  // platforms so nothing stale rides along to submit.
  const toggleDevice = useCallback((d: PublishDevice) => {
    setTarget((prev) => {
      if (prev.destination === 'template') {
        return { destination: 'audience', devices: [d], audienceDevices: [d] };
      }
      const devices = prev.devices.includes(d)
        ? prev.devices.filter((x) => x !== d)
        : [...prev.devices, d];
      return { destination: 'audience', devices, audienceDevices: devices };
    });
  }, []);

  const selectTemplateDestination = useCallback(() => {
    setTarget((prev) =>
      prev.destination === 'template'
        ? { destination: 'audience', devices: prev.audienceDevices, audienceDevices: prev.audienceDevices }
        : { destination: 'template', devices: [], audienceDevices: prev.devices },
    );
    setCurrentStepIndex(0);
  }, []);

  const goNext = useCallback(() => {
    setCurrentStepIndex((i) => i + 1);
  }, []);

  const goBack = useCallback(() => {
    setCurrentStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const goToStep = useCallback(
    (id: StepId) => {
      const idx = visibleSteps.indexOf(id);
      if (idx === -1) return;
      setCurrentStepIndex(idx);
    },
    [visibleSteps],
  );

  const reset = useCallback(() => {
    const start = startingState(initialDestination);
    setTarget(start);
    setCurrentStepIndex(startingStepIndex(start));
  }, [initialDestination, startingStepIndex]);

  return {
    destination,
    selectTemplateDestination,
    devices,
    toggleDevice,
    pathSteps,
    visibleSteps,
    currentStepIndex,
    currentStep,
    goNext,
    goBack,
    goToStep,
    canGoBack,
    canGoNext,
    isLastStep,
    reset,
  };
}
